import { Fragment, useState } from "react";

import { ActionIcon, Badge, Group, Loader, Menu, Stack } from "@mantine/core";

import {
  IconCheck,
  IconChevronDown,
  IconChevronDownLeft,
  IconPointFilled,
} from "@tabler/icons-react";

import { SERVICE_PAYER_CODE } from "@/billing/constants/service-payer-code";

import {
  INSURANCE_STATEMENT_STATUS_OPTIONS,
  SELF_PAY_STATEMENT_STATUS_OPTIONS,
  STATEMENT_ACCEPTED_SUB_STATUS_OPTIONS,
  STATEMENT_STATUS,
  STATEMENT_STATUS_BACKGROUND_COLOR,
  StatementStatus,
} from "./constants/statements";
import {
  getPrimaryStatementStatus,
  isAcceptedBranchStatus,
  statusDotColor,
} from "./utils/statements";

interface StatementStatusBadgeProps {
  /** The statement's Invoice id. It keys the badge test ids, so each row's badge is unique. */
  statementId: string;
  status: StatementStatus;
  payer: (typeof SERVICE_PAYER_CODE)[keyof typeof SERVICE_PAYER_CODE] | undefined;
  isSaving?: boolean;
  /**
   * Renders the badge as a static label with no menu. Set for a statement that has left the
   * billing lifecycle — deleted, restarted or archived — and for one whose status has settled at
   * Paid or Denied.
   */
  isReadOnly?: boolean;
  isBlocked?: boolean;
  onStatusChange: (status: StatementStatus) => void;
}

function getStatusBadgeRightSection({
  testId,
  disabled,
  isSaving,
}: {
  testId: string;
  disabled: boolean;
  isSaving: boolean;
}) {
  if (isSaving) return <Loader size={16} color="dark.9" data-testid={`${testId}-loader`} />;
  if (disabled) return null;

  return (
    <ActionIcon size={20} c="dark.9" variant="transparent" data-testid={`${testId}-chevron`}>
      <IconChevronDown size={20} />
    </ActionIcon>
  );
}

function StatusDisplayBadge({
  status,
  testId,
  nested = false,
  disabled = false,
  isSaving = false,
}: {
  status: StatementStatus;
  testId: string;
  nested?: boolean;
  disabled?: boolean;
  isSaving?: boolean;
}) {
  const badge = (
    <Badge
      fz="md"
      size="lg"
      c="dark.9"
      tt="capitalize"
      variant="filled"
      data-testid={testId}
      styles={{ label: { overflow: "visible" } }}
      color={STATEMENT_STATUS_BACKGROUND_COLOR[status]}
      rightSection={getStatusBadgeRightSection({ testId, disabled, isSaving })}
      style={{ cursor: disabled || isSaving ? "default" : "pointer", flexShrink: 0 }}
      leftSection={<IconPointFilled size={20} style={{ color: statusDotColor(status) }} />}
    >
      {status}
    </Badge>
  );

  if (!nested) return badge;

  return (
    <Group gap={4} wrap="nowrap">
      <IconChevronDownLeft size={24} style={{ color: "var(--mantine-color-gray-4)" }} />
      {badge}
    </Group>
  );
}

function StatusMenuOption({
  option,
  selectedStatus,
  nested = false,
  onClick,
}: {
  option: StatementStatus;
  selectedStatus: StatementStatus;
  nested?: boolean;
  onClick: () => void;
}) {
  return (
    <Menu.Item
      onClick={onClick}
      data-testid={`statement-status-option-${option}`}
      // Accepted expands its sub-status list, so it is the only option that keeps the menu open.
      closeMenuOnClick={option !== STATEMENT_STATUS.Accepted}
      rightSection={
        option === selectedStatus ? (
          <IconCheck size={20} style={{ color: "var(--mantine-color-black)" }} />
        ) : null
      }
      leftSection={
        <>
          {nested ? (
            <IconChevronDownLeft size={24} style={{ color: "var(--mantine-color-gray-4)" }} />
          ) : null}
          <IconPointFilled size={20} style={{ color: statusDotColor(option) }} />
        </>
      }
    >
      {option}
    </Menu.Item>
  );
}

export function StatementStatusBadge({
  statementId,
  status,
  payer,
  isSaving = false,
  isReadOnly = false,
  isBlocked = false,
  onStatusChange,
}: StatementStatusBadgeProps) {
  const isInsurance = payer === SERVICE_PAYER_CODE.Insurance;
  const isUnknownPayer = payer === undefined;
  const statusOptions = isInsurance
    ? INSURANCE_STATEMENT_STATUS_OPTIONS
    : SELF_PAY_STATEMENT_STATUS_OPTIONS;

  const [isChoosingSubStatus, setIsChoosingSubStatus] = useState(false);
  const primaryStatus = getPrimaryStatementStatus(status, payer);
  const acceptedSubStatus = primaryStatus === status ? null : status;
  const showAcceptedSubStatusOptions =
    isInsurance && (isAcceptedBranchStatus(status) || isChoosingSubStatus);

  const handleOptionClick = (option: StatementStatus) => {
    if (isInsurance && option === STATEMENT_STATUS.Accepted) {
      setIsChoosingSubStatus(true);
      return;
    }
    setIsChoosingSubStatus(false);
    onStatusChange(option);
  };

  // A statement whose status is not the biller's to change from here renders as a plain label with
  // no menu: one that has left the billing lifecycle (`isReadOnly`), one held pending data entry
  // (`isBlocked`), or one whose payer is unknown and so has no option list to offer.
  if (isUnknownPayer || isReadOnly || isBlocked) {
    return (
      <Stack gap="sm">
        <StatusDisplayBadge
          disabled
          status={primaryStatus}
          testId={`statement-status-badge-${statementId}`}
        />
        {acceptedSubStatus ? (
          <StatusDisplayBadge
            nested
            disabled
            status={acceptedSubStatus}
            testId={`statement-sub-status-badge-${statementId}`}
          />
        ) : null}
      </Stack>
    );
  }

  return (
    <Menu
      withArrow
      shadow="md"
      width={239}
      disabled={isSaving}
      position="bottom-start"
      onClose={() => setIsChoosingSubStatus(false)}
    >
      <Menu.Target>
        <Stack gap="sm">
          <StatusDisplayBadge
            isSaving={isSaving}
            status={primaryStatus}
            testId={`statement-status-badge-${statementId}`}
          />
          {acceptedSubStatus ? (
            <StatusDisplayBadge
              nested
              isSaving={isSaving}
              status={acceptedSubStatus}
              testId={`statement-sub-status-badge-${statementId}`}
            />
          ) : null}
        </Stack>
      </Menu.Target>
      <Menu.Dropdown>
        {statusOptions.map((option) => (
          <Fragment key={option}>
            {
              <StatusMenuOption
                option={option}
                selectedStatus={status}
                onClick={() => handleOptionClick(option)}
              />
            }
            {option === STATEMENT_STATUS.Accepted && showAcceptedSubStatusOptions
              ? STATEMENT_ACCEPTED_SUB_STATUS_OPTIONS.map((subOption) => (
                  <StatusMenuOption
                    nested
                    key={subOption}
                    option={subOption}
                    selectedStatus={status}
                    onClick={() => handleOptionClick(subOption)}
                  />
                ))
              : null}
          </Fragment>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}
